import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/UI/Dialog';
import { EmptyState } from '@/components/UI/State';
import { toast } from '@/components/UI/Toast';
import { Typography } from '@/components/UI/Typography';
import { fetchCompanyDocumentBlobAsync } from '@/http/DocumentManagement/DocumentManagement';
import { ExternalLink, FileText, FileX, Loader2 } from 'lucide-react';
import { FC, lazy, Suspense, useEffect, useMemo, useState } from 'react';

import { ComparableDocument, DocumentComparison } from './documentDiff';

// react-pdf / pdfjs-dist is loaded lazily (client-only) via this dynamic import. pdfjs evaluates
// `new DOMMatrix()` at import time, which Node has no implementation for, so a static import would
// crash the react-router dev server's SSR module evaluation (the build is `ssr: false`, but the dev
// server still evaluates route modules on the server). Deferring the import keeps it off that path;
// it only resolves in the browser, once a fetched PDF blob is actually being rendered.
const PdfInlinePreview = lazy(() => import('./PdfInlinePreview'));

type DocumentKind = 'pdf' | 'image' | 'unknown';

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg']);

const extensionFromName = (name: string | null): string | null => {
  if (!name) {
    return null;
  }
  const lastDot = name.lastIndexOf('.');
  return lastDot >= 0 ? name.slice(lastDot + 1).toLowerCase() : null;
};

/**
 * The file's type read from its own leading bytes (its magic number).
 *
 * This is the ONLY reliable signal here, and it is checked before the other two. The company-document
 * download endpoint returns a generic content type, so `blob.type` does not identify the file; and the
 * `documentName` fallback comes from the DIFF, which by design frequently has no filename to offer —
 * `extractDocumentComparisons` omits the unchanged `documentName` leaf on a same-name re-upload (see
 * `documentDiff.ts`), and the previous side of a first submission has no old value at all. With both
 * signals blank, every preview resolved to 'unknown' and the dialog showed "Preview not available for
 * this file type" for real, previewable PDFs.
 *
 * Magic numbers are position-fixed and content-type independent, so this works regardless of what the
 * server labels the response or whether the diff carried a filename.
 */
const sniffKindFromBytes = (bytes: Uint8Array): DocumentKind | null => {
  const startsWith = (...signature: number[]) =>
    signature.length <= bytes.length && signature.every((byte, i) => bytes[i] === byte);

  // "%PDF-"
  if (startsWith(0x25, 0x50, 0x44, 0x46)) {
    return 'pdf';
  }
  // PNG \x89PNG\r\n\x1a\n
  if (startsWith(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) {
    return 'image';
  }
  // JPEG SOI
  if (startsWith(0xff, 0xd8, 0xff)) {
    return 'image';
  }
  // GIF87a / GIF89a
  if (startsWith(0x47, 0x49, 0x46, 0x38)) {
    return 'image';
  }
  // RIFF....WEBP — the "WEBP" tag sits at offset 8, after the 4-byte size.
  if (
    startsWith(0x52, 0x49, 0x46, 0x46) &&
    bytes.length >= 12 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'image';
  }
  return null;
};

/** Enough leading bytes for every signature above (WebP needs 12). */
const SNIFF_BYTE_COUNT = 12;

/**
 * ERP Sync documents are not all PDFs (unlike the BEETasks certificate viewer, which only ever
 * handles PDFs). Resolution order, most to least trustworthy: the file's own magic number, then the
 * server's MIME type, then the `documentName` extension — see {@link sniffKindFromBytes} for why the
 * byte sniff has to come first.
 */
export const detectDocumentKind = (
  blob: Blob,
  documentName: string | null,
  sniffedBytes: Uint8Array | null
): DocumentKind => {
  const sniffed = sniffedBytes ? sniffKindFromBytes(sniffedBytes) : null;
  if (sniffed) {
    return sniffed;
  }
  if (blob.type === 'application/pdf') {
    return 'pdf';
  }
  if (blob.type.startsWith('image/')) {
    return 'image';
  }
  const extension = extensionFromName(documentName);
  if (extension === 'pdf') {
    return 'pdf';
  }
  if (extension && IMAGE_EXTENSIONS.has(extension)) {
    return 'image';
  }
  return 'unknown';
};

type FetchStatus = 'idle' | 'loading' | 'success' | 'error';

/** A muted placeholder used for the loading, error, and unsupported-type states. */
const PreviewPlaceholder: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-muted-foreground flex h-56 flex-col items-center justify-center gap-2 rounded-md bg-gray-50 p-4 text-center text-sm">
    {children}
  </div>
);

/**
 * One side (previous or current) of the comparison: fetches the document's bytes via the
 * authenticated download endpoint (the blob container is not publicly readable, #6838) and
 * renders it INLINE — a PDF via react-pdf, an image via an `<img>` object URL. Any other file
 * type (or a fetch/render failure) degrades to the "Open document" new-tab fallback, matching the
 * Agent Work Centre's BEETasks certificate viewer (`TaskViewModal`) but generalized beyond PDFs.
 */
const DocumentSide: FC<{
  title: string;
  document: ComparableDocument | null;
  emptyLabel: string;
}> = ({ title, document, emptyLabel }) => {
  const [isOpening, setIsOpening] = useState(false);
  const [status, setStatus] = useState<FetchStatus>('idle');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [pdfRenderError, setPdfRenderError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [sniffedBytes, setSniffedBytes] = useState<Uint8Array | null>(null);

  const documentId = document?.documentId ?? null;

  // Fetch the previewable bytes as soon as this side has a document to show.
  useEffect(() => {
    setPdfRenderError(false);
    if (!documentId) {
      setStatus('idle');
      setBlob(null);
      setSniffedBytes(null);
      return;
    }
    let cancelled = false;
    setStatus('loading');
    setBlob(null);
    setSniffedBytes(null);
    fetchCompanyDocumentBlobAsync(documentId)
      .then(async (fetchedBlob) => {
        // Read the leading bytes here rather than in the render pass: Blob.arrayBuffer() is async, and
        // the kind must be known before the first paint or the placeholder would flash "not available"
        // for a file that is in fact previewable. A slice() read is cheap — 12 bytes, no full decode.
        let leadingBytes: Uint8Array | null = null;
        try {
          const buffer = await fetchedBlob.slice(0, SNIFF_BYTE_COUNT).arrayBuffer();
          leadingBytes = new Uint8Array(buffer);
        } catch {
          // An unreadable slice is not a fetch failure — fall back to MIME/extension detection rather
          // than failing the whole preview.
          leadingBytes = null;
        }

        if (!cancelled) {
          setSniffedBytes(leadingBytes);
          setBlob(fetchedBlob);
          setStatus('success');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  const kind = useMemo(
    () =>
      blob
        ? detectDocumentKind(blob, document?.documentName ?? null, sniffedBytes)
        : null,
    [blob, document?.documentName, sniffedBytes]
  );

  // Object URLs must be revoked once they're no longer needed to avoid leaking memory.
  useEffect(() => {
    if (status !== 'success' || kind !== 'image' || !blob) {
      setImageUrl(null);
      return;
    }
    const url = globalThis.URL.createObjectURL(blob);
    setImageUrl(url);
    return () => {
      globalThis.URL.revokeObjectURL(url);
    };
  }, [status, kind, blob]);

  // Fetch via the authenticated download endpoint, then open as an object URL — the blob
  // container is not publicly readable (#6838), so a direct href cannot work. Reuses the blob
  // already fetched for the inline preview when available, to avoid a second request.
  const handleOpenDocument = async () => {
    if (!document || isOpening) return;
    setIsOpening(true);
    try {
      const openBlob = blob ?? (await fetchCompanyDocumentBlobAsync(document.documentId));
      const objectUrl = globalThis.URL.createObjectURL(openBlob);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toast({
        title: 'Error',
        description: 'Unable to open the document',
        color: 'danger'
      });
    } finally {
      setIsOpening(false);
    }
  };

  const openButton = (
    <button
      type="button"
      onClick={handleOpenDocument}
      disabled={isOpening}
      className="text-primary inline-flex items-center gap-1 underline disabled:opacity-50"
    >
      {isOpening ? 'Opening…' : 'Open document'}
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </button>
  );

  const renderPreview = () => {
    if (status === 'loading' || status === 'idle') {
      return (
        <PreviewPlaceholder>
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
          Loading preview…
        </PreviewPlaceholder>
      );
    }

    if (status === 'error') {
      return (
        <PreviewPlaceholder>
          <FileX className="h-6 w-6" aria-hidden />
          Couldn&apos;t preview this document.
        </PreviewPlaceholder>
      );
    }

    if (kind === 'pdf' && !pdfRenderError && blob) {
      const pdfLoading = (
        <PreviewPlaceholder>
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
          Loading preview…
        </PreviewPlaceholder>
      );
      return (
        <Suspense fallback={pdfLoading}>
          <PdfInlinePreview
            file={blob}
            onLoadError={() => setPdfRenderError(true)}
            loading={pdfLoading}
          />
        </Suspense>
      );
    }

    if (kind === 'image' && imageUrl) {
      return (
        <img
          src={imageUrl}
          alt={document?.documentName ?? 'Document preview'}
          className="max-h-64 w-full rounded-md object-contain"
        />
      );
    }

    // A file we DID identify as previewable that pdf.js then could not render. Reported separately
    // from the unsupported-type case below, which it used to fall through to: saying "not available for
    // this file type" about a correctly-identified PDF sends the reader off to investigate detection
    // when the failure is in rendering, and hides a broken viewer behind what looks like an unremarkable
    // unsupported file. Whatever pdf.js rejected, the bytes are still downloadable via "Open document".
    if (kind === 'pdf' && pdfRenderError) {
      return (
        <PreviewPlaceholder>
          <FileX className="h-6 w-6" aria-hidden />
          This PDF couldn&apos;t be displayed here. Use &quot;Open document&quot; to view
          it.
        </PreviewPlaceholder>
      );
    }

    // Genuinely unsupported type (e.g. a .docx) — no inline preview exists for it.
    return (
      <PreviewPlaceholder>
        <FileText className="h-6 w-6" aria-hidden />
        Preview not available for this file type.
      </PreviewPlaceholder>
    );
  };

  return (
    <div className="flex flex-col rounded-md border">
      <div className="border-b bg-gray-50 px-4 py-2">
        <Typography variant="subtitle2">{title}</Typography>
      </div>

      {!document ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <EmptyState icon={<FileX />} title={emptyLabel} />
        </div>
      ) : (
        <div className="flex flex-col gap-3 p-4 text-sm">
          <div className="flex items-start gap-2">
            <FileText
              className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0"
              aria-hidden
            />
            <span className="font-medium break-all">
              {document.documentName ?? 'Unnamed document'}
            </span>
          </div>
          {renderPreview()}
          {openButton}
        </div>
      )}
    </div>
  );
};

/**
 * Before/after document comparison view (PBI #6414, generalized to the whole-set-snapshot shape —
 * a diff can change several document TYPES at once, each compared independently). Shows the
 * previous (last-approved) and the current (proposed) document for ONE document type side by
 * side, each rendered INLINE (mirroring the Agent Work Centre's BEETasks certificate viewer) with
 * a fallback "Open document" new-tab action. A first submission with no previous document renders
 * a graceful empty state (AC3); a fetch/render failure on either side (e.g. the previous side's
 * blob has since been soft-deleted) degrades to a graceful per-side fallback rather than crashing
 * the dialog. The document bytes are fetched via the authenticated download endpoint — the blob
 * container is not publicly readable (#6838), so a direct href/src cannot be used.
 */
export const CompareDocumentsDialog: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentType: string;
  comparison: DocumentComparison;
}> = ({ open, onOpenChange, documentType, comparison }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl">
      <DialogHeader>
        <DialogTitle>Compare documents - {documentType}</DialogTitle>
        <DialogDescription>
          The last approved document and the newly submitted one, side by side.
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DocumentSide
          title="Previous"
          document={comparison.previous}
          emptyLabel="No previous document - this is the first submission."
        />
        <DocumentSide
          title="Current"
          document={comparison.current}
          emptyLabel="No current document to display."
        />
      </div>
    </DialogContent>
  </Dialog>
);
